import React, {useState} from 'react'
import {Grid, Typography} from '@mui/material'
import {Dialog, NumberDatePicker} from 'finsys-webcomponent'
import moment from 'moment'
import {useMutation} from 'react-query'

import {exportBTicketsSummary} from 'api/TS/query/InternalRequestQueries'
import {useSnackBar} from 'hoc/SnackbarHandler'
import MultipleSelect from 'components/InternalRequest/Summary/FilterOptions/MultipleSelect'

type ExportBAOBACDialogProps = {
  handleOnClose: () => void,
}

/**
 * A pop up dialog for Export component
 * @return {JSX.Element}
 * @constructor
 */
export const ExportBAOBACDialog = ({
  category,
  handleOnClose,
}: ExportBAOBACDialogProps) => {
  const {setSnackBar} = useSnackBar()

  const BCATEGORY_TYPE = ['Bank Account Opening', 'Bank Account Closing']

  const [exportCondition, setExportCondition] = useState({
    categories: [],
    last_update_date_from: moment('2021-01-01', 'YYYYMMDD'),
    last_update_date_to: moment(),
  })

  const handleOnExportChange = (key, value) => {
    setExportCondition((prevState) => ({
      ...prevState,
      [key]: value,
    }))
  }

  /**
   * To handle Export ticket
   * @type {UseMutationResult<DefaultSelectType, unknown, void, unknown>}
   */
  const exportTicket = useMutation(() => exportBTicketsSummary(exportCondition), {
    onSuccess: () => {
      setSnackBar({
        open: true,
        type: 'success',
        message: `Exported`,
      })
    },
    onError: (err) => {
      setSnackBar({
        open: true,
        type: 'info',
        message: 'No data available to export, please try other filters',
      })
      console.log(err)
    },
    onSettled: handleOnClose,
  })

  /**
   * To validate and export ticket
   */
  const handleExportTicket = () => {
    let isExportTicketValid = true

    /**
     * Convenient function to validate date
     * @param item
     * @param field
     */
    const toValidateDate = (item, field) => {
      if (
        (exportCondition[field] && !moment(exportCondition[field]).isValid()) ||
        exportCondition[`error_${field}`]
      ) {
        isExportTicketValid = false
      }
    }

    ;['last_update_date_from', 'last_update_date_to'].forEach((value) => {
      if (!exportCondition[value]) {
        isExportTicketValid = false

        setExportCondition((prevState) => ({
          ...prevState,
          [`error_${value}`]: 'This field is required',
        }))
      }

      if (['last_update_date_from', 'last_update_date_to'].includes(value)) {
        toValidateDate(exportCondition, value)
      }
    })

    if (isExportTicketValid) {
      exportTicket.mutate()
    } else {
      setSnackBar({
        open: true,
        type: 'error',
        message: 'Missing/ Invalid input! Please fill up all the required input',
      })
    }
  }

  return (
    <Dialog
      maxWidth={'lg'}
      handleOnClose={handleOnClose}
      buttons={[
        {
          label: 'Cancel',
          onClick: handleOnClose,
          type: 'text',
          disabled: exportTicket.isLoading,
        },
        {
          label: 'Export',
          onClick: handleExportTicket,
          type: 'primary',
          loading: exportTicket.isLoading,
        },
      ]}
      content={
        <Grid container justifyContent={'flex-end'} spacing={2}>
          <Grid item xs={12} container direction="row" columnSpacing={4} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                General
              </Typography>
            </Grid>
            <Grid container item direction={'row'}>
              <Grid
                container
                item
                xs={12}
                sm={12}
                md={12}
                direction={'row'}
                columnSpacing={4}
                rowSpacing={2}
              >
                <Grid item xs={12} sm={6} md={6}>
                  <MultipleSelect
                    isAll={true}
                    selections={['All', ...BCATEGORY_TYPE]}
                    // selections={[
                    //   'All',
                    //   ...category?.filter((value) => value !== '' && value !== null),
                    // ]}
                    limitTags={5}
                    label={'Select Category'}
                    onChange={(event, value) => {
                      if (value[value.length - 1] === 'All') {
                        setExportCondition((prevState) => ({
                          ...prevState,
                          categories:
                            exportCondition.categories.length === BCATEGORY_TYPE.length
                              ? []
                              : BCATEGORY_TYPE,
                        }))
                      } else {
                        setExportCondition((prevState) => ({
                          ...prevState,
                          categories: value,
                        }))
                      }
                    }}
                    value={exportCondition.categories}
                    getOptionLabel={(option) => {
                      return option
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} container direction="row" columnSpacing={4} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Dates
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <NumberDatePicker
                setSelectedDate={(value) => {
                  setExportCondition((prevState) => ({
                    ...prevState,
                    last_update_date_from: value
                      ? moment(value).format('yyyy-MM-DD')
                      : null,
                    error_last_update_date_from: null,
                  }))
                }}
                maxDate={moment(exportCondition.last_update_date_to)}
                value={exportCondition.last_update_date_from ?? null}
                label={'Select Last Updated on From'}
                required
                error={!!exportCondition.error_last_update_date_from}
                helperText={exportCondition.error_last_update_date_from}
                handleOnError={(error) => {
                  handleOnExportChange('error_last_update_date_from', error)
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <NumberDatePicker
                setSelectedDate={(value) => {
                  setExportCondition((prevState) => ({
                    ...prevState,
                    last_update_date_to: value
                      ? moment(value).format('yyyy-MM-DD')
                      : null,
                    error_last_update_date_to: null,
                  }))
                }}
                minDate={moment(exportCondition.last_update_date_from)}
                maxDate={moment(new Date())}
                value={exportCondition.last_update_date_to ?? null}
                label={'Select Last Updated on To'}
                required
                error={!!exportCondition.error_last_update_date_to}
                helperText={exportCondition.error_last_update_date_to}
                handleOnError={(error) => {
                  handleOnExportChange('error_last_update_date_to', error)
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      }
      disableBackdropClick
      title="Export BAO/BAC Tickets"
      type="info"
    />
  )
}
